import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ExerciseType, ExerciseTypeCreateInput, ExerciseTypeUpdateInput } from '../../types/exerciseType';
import { exerciseTypeService } from '../../services/exerciseTypeService';

const CATEGORY_LABELS: Record<string, { label: string; badgeClass: string }> = {
  cybersecurity: { label: 'Cybersecurity', badgeClass: 'bg-red-50 text-red-700 border-red-200' },
  natural_disaster: { label: 'Natural Disaster', badgeClass: 'bg-amber-50 text-amber-700 border-amber-200' },
  business_continuity: { label: 'Business Continuity', badgeClass: 'bg-blue-50 text-blue-700 border-blue-200' },
  physical_security: { label: 'Physical Security', badgeClass: 'bg-purple-50 text-purple-700 border-purple-200' },
  operational: { label: 'Operational', badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  general: { label: 'General Scenario', badgeClass: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const QUICK_ICONS = ['🔒', '🛡️', '📧', '✉️', '🌐', '🔗', '🔥', '🌋', '🌊', '⚡', '🏥', '🚨', '📋', '💻', '🏭', '🛰️'];
const QUICK_COLORS = ['#EF4444', '#F97316', '#EAB308', '#10B981', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#6B7280'];

export const ExerciseTypeManagement: React.FC = () => {
  const [types, setTypes] = useState<ExerciseType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Create / Edit modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingType, setEditingType] = useState<ExerciseType | null>(null);
  const [formData, setFormData] = useState<ExerciseTypeCreateInput>({
    key: '',
    name: '',
    description: '',
    category: 'cybersecurity',
    icon: '🔒',
    color: '#EF4444',
    display_order: 10,
    is_active: true,
  });
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Delete modal state
  const [deletingType, setDeletingType] = useState<ExerciseType | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await exerciseTypeService.getAdminExerciseTypes(true);
      setTypes(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load exercise types.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const filteredTypes = useMemo(() => {
    return types.filter((t) => {
      const matchesSearch =
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.key.toLowerCase().includes(search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [types, search, categoryFilter]);

  const handleOpenCreate = () => {
    setEditingType(null);
    setFormData({
      key: '',
      name: '',
      description: '',
      category: 'cybersecurity',
      icon: '🔒',
      color: '#EF4444',
      display_order: types.length + 1,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (t: ExerciseType) => {
    setEditingType(t);
    setFormData({
      key: t.key,
      name: t.name,
      description: t.description || '',
      category: t.category || 'general',
      icon: t.icon || '📋',
      color: t.color || '#4F46E5',
      display_order: t.display_order ?? 0,
      is_active: t.is_active,
    });
    setIsModalOpen(true);
  };

  const handleNameChange = (name: string) => {
    if (!editingType) {
      // Auto slugify key if creating new
      const autoKey = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      setFormData((prev) => ({ ...prev, name, key: autoKey }));
    } else {
      setFormData((prev) => ({ ...prev, name }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setSubmitting(true);
      setError(null);
      setMessage(null);

      if (editingType) {
        const updatePayload: ExerciseTypeUpdateInput = {
          name: formData.name.trim(),
          description: formData.description?.trim(),
          category: formData.category,
          icon: formData.icon,
          color: formData.color,
          display_order: Number(formData.display_order) || 0,
          is_active: formData.is_active,
        };
        await exerciseTypeService.updateExerciseType(editingType.id, updatePayload);
        setMessage(`Exercise type "${formData.name}" updated successfully.`);
      } else {
        const createPayload: ExerciseTypeCreateInput = {
          key: formData.key.trim().toLowerCase(),
          name: formData.name.trim(),
          description: formData.description?.trim(),
          category: formData.category,
          icon: formData.icon,
          color: formData.color,
          display_order: Number(formData.display_order) || 0,
          is_active: formData.is_active,
        };
        await exerciseTypeService.createExerciseType(createPayload);
        setMessage(`Exercise type "${formData.name}" created successfully.`);
      }

      setIsModalOpen(false);
      await fetchTypes();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save exercise type.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    try {
      setDeleting(true);
      setError(null);
      const res = await exerciseTypeService.deleteExerciseType(deletingType.id);
      setMessage(res.message);
      setDeletingType(null);
      await fetchTypes();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete exercise type.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (t: ExerciseType) => {
    try {
      await exerciseTypeService.updateExerciseType(t.id, { is_active: !t.is_active });
      setTypes((prev) => prev.map((item) => (item.id === t.id ? { ...item, is_active: !t.is_active } : item)));
      setMessage(`Exercise type "${t.name}" is now ${!t.is_active ? 'active' : 'inactive'}.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update status.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {message && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-medium">{message}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-emerald-500 hover:text-emerald-700 text-sm">
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 text-sm">
            ×
          </button>
        </div>
      )}

      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Tabletop Exercise Types</h2>
          <p className="text-sm text-slate-500">
            Manage exercise categories, custom scenario types, icons, and active availability for scenario creators.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenCreate}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-900 hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Exercise Type
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-80">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, slug or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Category:</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Categories</option>
            <option value="cybersecurity">Cybersecurity</option>
            <option value="natural_disaster">Natural Disaster</option>
            <option value="business_continuity">Business Continuity</option>
            <option value="physical_security">Physical Security</option>
            <option value="operational">Operational</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Types Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">Loading exercise types...</div>
        ) : filteredTypes.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-600 font-medium">No exercise types found</p>
            <p className="text-slate-400 text-sm mt-1">Try adjusting your search or category filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="py-3 px-4">Exercise Type</th>
                  <th className="py-3 px-4">Key / Slug</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredTypes.map((t) => {
                  const catConfig = CATEGORY_LABELS[t.category || 'general'] || CATEGORY_LABELS.general;
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-3">
                          <span
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shadow-xs border"
                            style={{ backgroundColor: `${t.color || '#4F46E5'}15`, borderColor: `${t.color || '#4F46E5'}30` }}
                          >
                            {t.icon || '📋'}
                          </span>
                          <div>
                            <span className="font-semibold text-slate-900 block">{t.name}</span>
                            {t.is_system && (
                              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                                Built-in
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-xs text-slate-600">
                        <span className="bg-slate-100 px-2 py-0.5 rounded border border-slate-200">{t.key}</span>
                      </td>

                      <td className="py-3.5 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${catConfig.badgeClass}`}>
                          {catConfig.label}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 max-w-xs truncate text-xs" title={t.description || ''}>
                        {t.description || <span className="text-slate-400 italic">No description provided</span>}
                      </td>

                      <td className="py-3.5 px-4">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(t)}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${
                            t.is_active
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          title="Click to toggle active status"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${t.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                          {t.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(t)}
                            className="p-1.5 text-slate-500 hover:text-indigo-900 rounded-md hover:bg-slate-100 transition-colors"
                            title="Edit Exercise Type"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingType(t)}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                            title="Delete or Deactivate"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="font-bold text-slate-900 text-base">
                {editingType ? `Edit Exercise Type: ${editingType.name}` : 'Create New Exercise Type'}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-xl font-light leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                  Type Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ransomware Outbreak, Cloud Disruption"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                  Identifier / Slug Key *
                </label>
                <input
                  type="text"
                  required
                  disabled={Boolean(editingType)}
                  placeholder="e.g. ransomware_outbreak"
                  value={formData.key}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-75 disabled:cursor-not-allowed"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Unique programmatic identifier used across scenarios and templates. Cannot be modified after creation.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="cybersecurity">Cybersecurity</option>
                    <option value="natural_disaster">Natural Disaster</option>
                    <option value="business_continuity">Business Continuity</option>
                    <option value="physical_security">Physical Security</option>
                    <option value="operational">Operational</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => setFormData((prev) => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Icon & Color Selection */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                  Icon (Emoji or Symbol)
                </label>
                <div className="flex items-center space-x-3 mb-2">
                  <span
                    className="w-10 h-10 rounded-lg border flex items-center justify-center text-xl"
                    style={{ backgroundColor: `${formData.color || '#4F46E5'}15`, borderColor: `${formData.color || '#4F46E5'}30` }}
                  >
                    {formData.icon || '📋'}
                  </span>
                  <input
                    type="text"
                    maxLength={10}
                    value={formData.icon || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
                    placeholder="Emoji"
                    className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <div className="flex flex-wrap gap-1">
                    {QUICK_ICONS.slice(0, 8).map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, icon: emoji }))}
                        className="w-7 h-7 text-sm rounded hover:bg-slate-100 flex items-center justify-center border border-slate-200"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                  Accent Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.color || '#4F46E5'}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    className="w-9 h-9 rounded cursor-pointer border border-slate-300 p-0.5"
                  />
                  <input
                    type="text"
                    value={formData.color || '#4F46E5'}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    className="w-28 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-mono"
                  />
                  <div className="flex items-center space-x-1.5">
                    {QUICK_COLORS.map((hex) => (
                      <button
                        key={hex}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, color: hex }))}
                        className="w-5 h-5 rounded-full border border-slate-200 transition-transform hover:scale-110"
                        style={{ backgroundColor: hex }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  placeholder="Explain what threats, objectives, or scenarios this exercise tests..."
                  value={formData.description || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="pt-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-slate-300 text-indigo-900 focus:ring-indigo-500 h-4 w-4"
                  />
                  <span className="text-sm font-medium text-slate-700">Active (available for selection in exercises)</span>
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-indigo-900 text-white rounded-lg text-sm font-medium hover:bg-indigo-800 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingType ? 'Save Changes' : 'Create Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete / Deactivate Confirmation Modal */}
      {deletingType && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            <h3 className="font-bold text-slate-900 text-base mb-2">
              Remove Exercise Type "{deletingType.name}"?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              If this exercise type is currently referenced by historical tabletop exercises or templates, it will be safely marked <strong>Inactive</strong> to prevent data corruption. If unreferenced, it will be deleted permanently.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeletingType(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseTypeManagement;
